import { pickLocale, type MultiselectCharacteristic, type SelectCharacteristic } from '../../schema';
import { useCharacteristicSelection, useStoreActions } from '../../state/StoreContext';
import { useRuleResults } from '../../rules/RuleResultsContext';
import { useEffectiveValuePrice, useValuePriceFormatter } from '../../state/ComponentContext';
import { useTranslation } from '../../i18n';
import { QtyInput } from './QtyInput';

interface SelectViewProps {
    characteristic: SelectCharacteristic | MultiselectCharacteristic;
    locale: string | undefined;
}

/**
 * Interactive `<select>` / multi-select. Reads its current pick(s) from the
 * store and dispatches selectValue / toggleValue on change.
 */
export function SelectView({ characteristic, locale }: SelectViewProps) {
    const { t } = useTranslation();
    const isMulti = characteristic.type === 'multiselect';
    const selection = useCharacteristicSelection(characteristic.id);
    const { selectValue, toggleValue, setPickQty } = useStoreActions();
    const { blacklistedValues } = useRuleResults();
    const formatValuePrice = useValuePriceFormatter();
    const effectivePrice = useEffectiveValuePrice();

    const singleValue =
        selection?.kind === 'single' && selection.valueId !== null
            ? String(selection.valueId)
            : '';
    const multiValues =
        selection?.kind === 'multi' ? selection.valueIds.map(String) : [];

    const placeholder = t('select.placeholder');

    // Relative-mode base: current single pick's effective price (multi-select has none).
    const currentPickValue = !isMulti
        ? characteristic.values.find((v) => String(v.id) === singleValue)
        : undefined;
    const currentPickPrice = currentPickValue
        ? effectivePrice(currentPickValue, characteristic)
        : undefined;

    // Blacklisted Values are hidden entirely (default storefront behaviour).
    const visibleValues = characteristic.values.filter(
        (v) => !blacklistedValues.has(v.id),
    );

    const qtyMode = !isMulti ? characteristic.quantityMode : undefined;
    const select = (
        <select
            className="cfg-select"
            multiple={isMulti}
            size={isMulti ? Math.min(characteristic.values.length || 1, 5) : undefined}
            aria-label={pickLocale(characteristic.title, locale) ?? t('characteristic.aria_select')}
            value={isMulti ? multiValues : singleValue}
            onChange={(e) => {
                if (isMulti) {
                    // Determine which value just toggled by diffing the new selected
                    // list against the prior store snapshot.
                    const nextIds = Array.from(e.target.selectedOptions).map((o) =>
                        Number(o.value),
                    );
                    const prior = selection?.kind === 'multi' ? selection.valueIds : [];
                    const added = nextIds.find((id) => !prior.includes(id));
                    const removed = prior.find((id) => !nextIds.includes(id));
                    const changed = added ?? removed;
                    if (changed !== undefined) toggleValue(characteristic.id, changed);
                } else {
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    selectValue(characteristic.id, v);
                }
            }}
        >
            {!isMulti && (
                <option value="">{placeholder}</option>
            )}
            {visibleValues.map((value) => {
                const label = pickLocale(value.title, locale) ?? value.value ?? `#${value.id}`;
                const info = pickLocale(value.info, locale);
                const priceSuffix = formatValuePrice(effectivePrice(value, characteristic), currentPickPrice);
                // <option> can't render HTML — strip tags for the native
                // tooltip and let admins put rich info on radio/checkbox
                // instead. Plain-text info still shows on hover.
                const plainInfo = info ? info.replace(/<[^>]+>/g, '').trim() : '';
                return (
                    <option
                        key={value.id}
                        value={value.id}
                        title={plainInfo || undefined}
                    >
                        {label}
                        {priceSuffix ? ` ${priceSuffix}` : ''}
                    </option>
                );
            })}
        </select>
    );

    if (!qtyMode) return select;
    return (
        <div className="cfg-qty-row">
            <QtyInput
                mode={qtyMode}
                min={characteristic.minQty}
                max={characteristic.maxQty}
                value={selection?.kind === 'single' ? selection.qty : undefined}
                onChange={(qty) => setPickQty(characteristic.id, qty)}
                label={pickLocale(characteristic.title, locale) ?? ''}
            />
            {select}
        </div>
    );
}
